import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Alert, Chip, Stack, Card, CardContent } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { cashPaymentsService } from '../../services/cashPayments.service';
import CashPaymentDialogV2 from '../../components/vouchers/CashPaymentDialogV2';
import { CreateVoucherDto, UpdateVoucherDto } from '../../types/vouchers.types';
import config from '../../config';
import axios from 'axios';

/**
 * EXPERIMENTAL — Smart Entry V2 Sandbox
 *
 * Isolated page for prototyping the new voice-driven voucher dialog.
 * NOT linked to production cash-payments listing on purpose: vouchers saved
 * here go to the same DB so the user can verify end-to-end, but the page
 * itself stays out of the day-to-day flow until we promote it.
 *
 * Open at: /smart-entry-v2
 */
const SmartEntryV2Page: React.FC = () => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';

    // Same SA gate as CashPaymentsPageV2 — sandbox stays Super-Admin-only.
    const isSuperAdmin = (() => {
        try {
            const raw = localStorage.getItem('user');
            if (!raw) return false;
            const u = JSON.parse(raw);
            return u?.isSuperAdmin === true;
        } catch { return false; }
    })();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [cashParentAccountId, setCashParentAccountId] = useState<number | null>(null);
    const [lastSavedRef, setLastSavedRef] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                // Mirrors CashPaymentsPage.tsx: read the cash parent from
                // SystemSettings (NOT /AccountingSettings — that endpoint exposes a
                // different shape and was returning null here, leaving the credit-side
                // dropdown unfiltered).
                const resp = await axios.get(`${config.API_BASE_URL}/SystemSettings/CashParentAccountId`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const raw = resp.data?.value;
                if (!cancelled && raw) setCashParentAccountId(parseInt(raw, 10));
            } catch {
                /* settings load failure is non-fatal here — dialog falls back to defaults */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleSave = async (dto: CreateVoucherDto | UpdateVoucherDto) => {
        try {
            const result = await cashPaymentsService.create(dto as CreateVoucherDto);
            setLastSavedRef(result?.voucherNumber ?? 'saved');
            toast.success(
                isRTL
                    ? `تم الحفظ في V2 — ${result?.voucherNumber ?? ''}`
                    : `Saved via V2 — ${result?.voucherNumber ?? ''}`
            );
            setDialogOpen(false);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Save failed');
            throw err;
        }
    };

    if (!isSuperAdmin) return <Navigate to="/accounting/cash-payments" replace />;

    return (
        <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
            <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'warning.50', borderLeft: '6px solid', borderColor: 'warning.main' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <ScienceIcon color="warning" sx={{ fontSize: 48 }} />
                    <Box flex={1}>
                        <Typography variant="h5" fontWeight="bold">
                            {isRTL ? '🧪 Smart Entry V2 — صفحة تجريبية' : '🧪 Smart Entry V2 — Sandbox'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isRTL
                                ? 'نسخة تجريبية للمساعد الذكي الجديد. أي تعديل هنا قابل للحذف بأمان. السندات المحفوظة تظل في نفس قاعدة البيانات.'
                                : 'Experimental sandbox for the upgraded smart assistant. Safe to delete this page when done. Saved vouchers go to the same DB.'}
                        </Typography>
                    </Box>
                    <Chip label="V2" color="warning" />
                </Stack>
            </Paper>

            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {isRTL ? 'ما الجديد في V2' : "What's new in V2"}
                    </Typography>
                    <Stack spacing={1} sx={{ pl: 2 }}>
                        <Typography variant="body2">
                            • {isRTL ? 'نطق حر، عربي/إنجليزي، يحلل تلقائياً ويترجم.' : 'Free dictation, AR/EN, auto-translate & analyse.'}
                        </Typography>
                        <Typography variant="body2">
                            • {isRTL ? 'نتيجة افتراضية + 5 بدائل قابلة للاختيار لكل حساب.' : 'Default suggestion + 5 alternatives per account field.'}
                        </Typography>
                        <Typography variant="body2">
                            • {isRTL ? 'Keywords ذكية: "التاريخ ..."، "المستفيد ..."، "سطر 2 ..." تستهدف الحقل المنطوق.' : 'Smart keywords: "date …", "payee …", "line 2 …" target the spoken field.'}
                        </Typography>
                        <Typography variant="body2">
                            • {isRTL ? 'Active Line indicator يبيّن السطر النشط.' : 'Active line indicator highlights the focused row.'}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ py: 1.5, px: 3, fontWeight: 'bold' }}
                >
                    {isRTL ? 'افتح Smart Entry V2' : 'Open Smart Entry V2'}
                </Button>
                {lastSavedRef && (
                    <Alert severity="success" sx={{ flex: 1 }}>
                        {isRTL ? `آخر سند محفوظ: ${lastSavedRef}` : `Last saved voucher: ${lastSavedRef}`}
                    </Alert>
                )}
            </Box>

            {dialogOpen && (
                <CashPaymentDialogV2
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onSave={handleSave}
                    editVoucher={null}
                    cashParentAccountId={cashParentAccountId ?? 0}
                />
            )}
        </Box>
    );
};

export default SmartEntryV2Page;
